import type { Schema } from "./api";
import { documentWorks, type DocumentDelivery } from "./documentDelivery.ts";

/** Scope rules of the «Выгрузить» dialog without React, so they run under the plain Node test runner.
 * The server stays authoritative (export_scope.py); these mirror it so the dialog counts what will be exported. */
export type ScopeKey = "building" | "discipline" | "category" | "group" | "description";
export type ExportWork = Pick<Schema["WorkOut"], "id" | "status" | "quantity" | ScopeKey>;
export type ExportState = { useSelection: boolean; selectedIds: string[]; document_id?: string } & Record<ScopeKey, string>;
export type ExportFormat = Schema["ExportInput"]["format"];

export const scopeKeys: ScopeKey[] = ["building", "discipline", "category", "group", "description"];
export const emptyScope: Record<ScopeKey, string> = { building: "", discipline: "", category: "", group: "", description: "" };

export const formatNames: Record<string, string> = {
  full_estimate: "Смета", direct_costs: "Прямые затраты (не итоговая смета)", package: "Полный комплект (ZIP)",
  xlsx: "ВОР · Excel", docx: "ВОР · Word", kac: "Материалы и запрос КАЦ", protocol: "Протокол расхождений",
  information: "Информационная справка", unknowns: "Неизвестные и неподтверждённые данные", grand: "ВОР для ГРАНД-Сметы",
};

/** Same trimming as the server: a blank field does not select an empty building. */
export function scopeValues(state: Record<ScopeKey, string>): Partial<Record<ScopeKey, string>> {
  const result: Partial<Record<ScopeKey, string>> = {};
  for (const key of scopeKeys) { const value = state[key].trim(); if (value) result[key] = value; }
  return result;
}

export function matchesScope(work: ExportWork, scope: Partial<Record<ScopeKey, string>>): boolean {
  return scopeKeys.every(key => scope[key] === undefined || work[key] === scope[key]);
}

/** Rejected works never leave the workbench; a selection replaces the scope fields entirely. */
export function includedWorks<T extends ExportWork>(rows: T[], state: ExportState, delivery?: DocumentDelivery): T[] {
  const active = rows.filter(work => work.status !== "rejected");
  if (state.useSelection) { const chosen = new Set(state.selectedIds); return active.filter(work => chosen.has(work.id)); }
  const scope = scopeValues(state);
  return documentWorks(active, delivery, state.document_id || "").filter(work => matchesScope(work, scope));
}

export function exportCounts(included: ExportWork[]) {
  return {
    works: included.length,
    pending: included.filter(work => work.status !== "approved").length,
    unknown: included.filter(work => work.quantity == null).length,
    sections: new Set(included.map(work => work.discipline)).size,
  };
}

export function exportBody(state: ExportState, options: { format: ExportFormat; draft: boolean; before: string }): Schema["ExportInput"] {
  const scope = state.useSelection ? {} : scopeValues(state);
  return {
    format: options.format, draft: options.draft, before: options.before || null,
    building: scope.building ?? null, discipline: scope.discipline ?? null, category: scope.category ?? null,
    group: scope.group ?? null, description: scope.description ?? null,
    work_id: state.useSelection ? state.selectedIds : null,
    ...(!state.useSelection && state.document_id ? { document_id: state.document_id } : {}),
  };
}

/** Values offered by one scope field. Building and section list the whole revision; the fields below narrow. */
const narrowedBy: Record<ScopeKey, ScopeKey[]> = {
  building: [], discipline: [], category: ["discipline"], group: ["discipline", "category"],
  description: ["building", "discipline", "category", "group"],
};
export function scopeOptions(rows: ExportWork[], state: Record<ScopeKey, string>, key: ScopeKey): string[] {
  const chosen = scopeValues(state), above: Partial<Record<ScopeKey, string>> = {};
  for (const parent of narrowedBy[key]) if (chosen[parent] !== undefined) above[parent] = chosen[parent];
  const values = rows.filter(work => work.status !== "rejected" && matchesScope(work, above)).map(work => work[key]).filter(Boolean);
  return [...new Set(values)].sort((a, b) => a.localeCompare(b, "ru", { numeric: true }));
}

/** The one hierarchy a selection shares, shown so a subset is recognizable without claiming the whole type. */
export function selectionLabel(included: ExportWork[]): string {
  return scopeKeys.slice(0, 4).map(key => {
    const values = [...new Set(included.map(work => work[key]))];
    return values.length === 1 ? values[0] : "";
  }).filter(Boolean).join(" / ");
}

export function scopeText(scope: Record<string, unknown>): string {
  return ["document", ...scopeKeys, "works"].map(key => scope[key]).filter(Boolean).join(" / ") || "Весь проект";
}

/** СВОР covers an estimate section as a whole, so only the project-wide package carries it. */
export function packageIncludesSvor(state: ExportState): boolean {
  return !state.useSelection && !state.document_id && Object.keys(scopeValues(state)).length === 0;
}
